import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// ANT Design
import { Button, App, Empty, notification, Tree, message } from 'antd';
import Tooltip from '../common/Tooltip';
import { anyRemoteLockHolder } from '../../collab/structureLocks';

import { ItemTypes, PANEL_LIGHT_BG, PRIMARY_DARK, getPrimaryDark } from '../../constants/constants';
import { useStore } from '../../store/store';
import './LeftSide.less';
import { displayTitleIndex, getTitlesConfig, numberedTitleLevels, selectLevelTitle, titleDisplayIndex } from '../../utils/titles';
import { scrollToItem } from '../Middle/InfinitePage';
import { useContainersToShow, useDocumentKindLabel } from '../../hooks';
import { isATitleBox } from '../../utils/boxes';
import { findOwnerTitleId, getDirectLinkedSectionId, isSummaryTitleHighlighted } from '../../utils/linkedSectionHighlight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { DownOutlined, ExclamationCircleFilled } from '@ant-design/icons';

const TITLE_TYPE_MAP = {
  0: ItemTypes.TITLE,
  1: ItemTypes.TITLE_2,
  2: ItemTypes.TITLE_3,
  3: ItemTypes.TITLE_4,
};

export const generateTreeFromFlatBoxes = ({ boxes, separator, titlesTypeConfig, numberedLevels, finalSeparator, columns }) => {
  if (!boxes) return [];

  const boxMap = {};

  // Fonction pour créer un parent manquant avec des données vides
  const createMissingParent = (indexTitleArray, childBox) => {
    const parentKey = indexTitleArray.join('-');
    if (boxMap[parentKey]) {
      return parentKey;
    }

    // Déterminer le type de titre du parent (niveau 1 = TITLE, niveau 2 = TITLE_2, etc.)
    const titleTypeMap = {
      0: ItemTypes.TITLE,
      1: ItemTypes.TITLE_2,
      2: ItemTypes.TITLE_3,
      3: ItemTypes.TITLE_4,
    };
    const parentType = titleTypeMap[indexTitleArray.length - 1] || ItemTypes.TITLE;

    // Créer un titre vide pour le parent
    const parentTitle = displayTitleIndex(
      indexTitleArray,
      separator,
      titlesTypeConfig,
      numberedLevels,
      finalSeparator
    ) + ''; // Titre vide

    // Créer le parent manquant
    boxMap[parentKey] = {
      key: `missing-parent-${parentKey}`, // Clé unique pour les parents manquants
      title: parentTitle,
      column: childBox.column || columns.find((column) => column.id === childBox.columnId),
      indexTitle: indexTitleArray,
      type: parentType,
      children: [],
      isMissingParent: true, // Marqueur pour indiquer que c'est un parent manquant
    };

    // Si le parent a lui-même un parent, créer récursivement les parents manquants
    if (indexTitleArray.length > 1) {
      const grandParentKey = createMissingParent(
        indexTitleArray.slice(0, -1),
        childBox
      );
      if (grandParentKey && boxMap[grandParentKey]) {
        boxMap[grandParentKey].children.push(boxMap[parentKey]);
      }
    }

    return parentKey;
  };

  const checkIfParentExist = (key) => {
    if (!key || key.length === 0) return null; // Gestion des valeurs nulles ou vides

    if (!boxMap[key]) {
      // Recherche de l'index du parent
      const newParentKey = key.split('-').slice(0, -1).join('-');
      return checkIfParentExist(newParentKey);
    }
    return key;
  };

  // Construction de la structure arborescente
  boxes.forEach((box) => {
    if (!isATitleBox(box.type)) return;

    // Création des clés de parent et d'élément courant
    let parentKey = box.indexTitle.slice(0, box.indexTitle.length - 1).join('-');
    const currentKey = box.indexTitle.join('-');
    const originalParentKey = parentKey;
    parentKey = checkIfParentExist(parentKey);

    // Création ou récupération de la box courante
    if (!boxMap[currentKey]) {
      const index = displayTitleIndex(
        titleDisplayIndex(box.content),
        separator,
        titlesTypeConfig,
        numberedLevels,
        finalSeparator
      );
      // Sans séparateur final, le numéro se collerait au texte ("06Qualité").
      const title = index && finalSeparator === false
        ? `${index} ${box.text}`
        : index + box.text;
      boxMap[currentKey] = {
        ...box,
        key: box.id,
        title,
        column: columns.find((column) => column.id === box.columnId),
        indexTitle: box.indexTitle,
        children: []
      };
    }

    // Ajout de l'élément courant comme enfant de son parent
    // Si le titre a un parent (indexTitle.length > 1), créer le parent s'il n'existe pas
    if (box.indexTitle.length > 1) {
      // Si le parent n'existe pas, le créer avec des données vides
      if (!boxMap[originalParentKey]) {
        const parentIndexTitle = box.indexTitle.slice(0, box.indexTitle.length - 1);
        createMissingParent(parentIndexTitle, boxMap[currentKey]);
      }
      // S'assurer que le parent existe maintenant
      if (boxMap[originalParentKey]) {
        // Vérifier que l'enfant n'est pas déjà dans les enfants du parent
        if (!boxMap[originalParentKey].children.find(child => child.key === boxMap[currentKey].key)) {
          boxMap[originalParentKey].children.push(boxMap[currentKey]);
        }
      }
    }
  });

  const markLastChildren = (children) => {
    if (!children || children.length === 0) return;
    children.forEach((child, index) => {
      child.isLast = index === children.length - 1; // Dernier élément
      if (child.children && child.children.length > 0) {
        markLastChildren(child.children); // Répéter pour les niveaux imbriqués
      }
    });
  };

  // Filtre pour retourner uniquement les éléments de niveau racine
  // Inclure maintenant aussi les parents manquants de niveau 1
  const rootBoxes = Object.values(boxMap).filter((box) => {
    return box?.indexTitle?.length === 1;
  });

  rootBoxes.forEach((root) => {
    markLastChildren(root.children);
  });

  return rootBoxes;
};

const selector = (state) => ({
  boxes: state.getSummaryBoxes(),
  updateIndexTitle: state.updateIndexTitle,
  updateBox: state.updateBox,
  batchUpdateBox: state.batchUpdateBox,
  moveBoxesIndex: state.moveBoxesIndex,
  defaultConfiguration: state.configuration,
  allBoxes: state.boxes,
  columns: state.columns,
  containers: state.containers,
  removeSectionContainers: state.removeSectionContainers,
  removeContainer: state.removeContainer,
  removePdfImportContainers: state.removePdfImportContainers,
  selectedBoxId: state.selectedBoxId,
  selectedContainer: state.selectedContainer,
  currentDocument: state.currentDocument,
});
const itemSelector =
  () =>
    ({ configuration, unCollapseTitles }) => ({
      separator: configuration.separatorTitle,
      numberedLevels: numberedTitleLevels(configuration),
      finalSeparator: configuration.finalSeparatorTitle,
      titlesTypeConfig: getTitlesConfig(configuration),
      unCollapseTitles
    });

const runSummaryDeleteConfirm = (modalApi, { deleteContainer, deleteSectionContainers,
  deleteAllContainerFromPdfToImageWithSameKey, t, fromSection,
  KeyPdfToImage, fromLinkedSection, message = 'title-delete-warning' }) => {

  const flexDirectionButton = (fromSection || KeyPdfToImage) ? "column" : "row"

  const modalInst = modalApi.confirm({
    title: t(message),
    icon: <ExclamationCircleFilled />,
    cancelText: t('cancel'),
    okText: t('delete'),
    okType: 'danger',
    onOk() {
      deleteContainer();
    },
    onCancel() {
      // Handle onCancel logic if needed
    },
    footer: (
      <div style={{ display: "flex", flexDirection: flexDirectionButton, justifyContent: "end", gap: 10 }}>
        <Button onClick={() => modalInst.destroy()}>{t('cancel')}</Button>
        {fromSection && <Button danger onClick={async () => {
          await deleteSectionContainers()
          modalInst.destroy()
        }
        }>
          {t("section-delete-all-container")}
        </Button>}
        {KeyPdfToImage && <Button danger onClick={async () => {
          await deleteAllContainerFromPdfToImageWithSameKey()
          modalInst.destroy()
        }
        }>
          {t("pdf-to-image-delete-all-container")}
        </Button>}
        <Button danger disabled={fromLinkedSection} onClick={async () => {
          await deleteContainer()
          modalInst.destroy()
        }
        }>{t('delete')}</Button>
      </div>
    ),
  });
};

const SummarySide = ({ selectedSummaryTitle, onSummaryTitleSelect }) => {
  const { modal: modalApi } = App.useApp();
  const { boxes, updateIndexTitle, updateBox, batchUpdateBox, moveBoxesIndex, defaultConfiguration, allBoxes, columns, containers, removeSectionContainers, removeContainer, removePdfImportContainers, selectedBoxId, selectedContainer, currentDocument } = useStore(selector);
  const { separator, titlesTypeConfig, numberedLevels, finalSeparator, unCollapseTitles } = useStore(
    itemSelector()
  );

  // useMemo instead of useState+useEffect: synchronous, no extra render cycle,
  // and now correctly depends on all inputs (separator/columns were missing before).
  const gData = useMemo(
    () => generateTreeFromFlatBoxes({ boxes, separator, titlesTypeConfig, numberedLevels, finalSeparator, columns }),
    [boxes, separator, titlesTypeConfig, numberedLevels, finalSeparator, columns]
  );

  const documentKindLabel = useDocumentKindLabel();

  const [api, contextHolder] = notification.useNotification();
  const [currentDragTitle, setCurrentDragTitle] = useState(null);

  // Quand une box est sélectionnée dans le document :
  // - si c'est un titre → on le surligne directement
  // - sinon → on remonte les containers pour trouver le titre propriétaire
  // La section liée est détectée directement depuis la box/container (indépendamment du titre trouvé)
  useEffect(() => {
    if (!selectedBoxId) return;
    const box = allBoxes.find((b) => b.id === selectedBoxId);
    if (!box) return;

    let titleId;
    if (isATitleBox(box.type)) {
      titleId = box.id;
    } else {
      titleId = findOwnerTitleId({ boxId: selectedBoxId, allBoxes, containers });
    }

    const linkedSectionId = getDirectLinkedSectionId({ boxId: selectedBoxId, allBoxes, containers });
    const titleBox = titleId ? allBoxes.find((b) => b.id === titleId) : null;

    onSummaryTitleSelect(titleId ? {
      key: titleId,
      id: titleId,
      fromSectionId: titleBox?.fromSectionId,
      from_linked_section: titleBox?.from_linked_section,
      linkedSectionId,
    } : null);
  }, [selectedBoxId, allBoxes, containers, onSummaryTitleSelect]);

  // Quand un container est sélectionné sans box → trouver le titre propriétaire
  useEffect(() => {
    if (selectedBoxId) return;
    if (!selectedContainer) {
      onSummaryTitleSelect(null);
      return;
    }
    const titleId = findOwnerTitleId({ containerId: selectedContainer.id, allBoxes, containers });
    const linkedSectionId = getDirectLinkedSectionId({ containerId: selectedContainer.id, allBoxes, containers });
    const titleBox = titleId ? allBoxes.find((b) => b.id === titleId) : null;

    onSummaryTitleSelect(titleId ? {
      key: titleId,
      id: titleId,
      fromSectionId: titleBox?.fromSectionId,
      from_linked_section: titleBox?.from_linked_section,
      linkedSectionId,
    } : null);
  }, [selectedContainer, selectedBoxId, allBoxes, containers, onSummaryTitleSelect]);

  const openNotification = useCallback((message) => {
    api.warning({
      message: 'Déplacement interdit',
      description: message,
      placement: 'top',
    });
  }, [api]);

  const [expandedKeys] = useState();

  const { t } = useTranslation();
  const containersToShow = useContainersToShow()

  const onDragEnter = useCallback(() => {}, []);
  const onDrop = async (info) => {
    // Collab : un drag de titre réordonne tout un bloc → bloqué entièrement
    // (move + changement de niveau) si un co-éditeur édite quoi que ce soit
    const remote = anyRemoteLockHolder(useStore.getState());
    if (remote) {
      message.warning(`Déplacement impossible : ${remote.name} édite le document`);
      return;
    }
    const dropKey = info.node.key;
    const dragKey = info.dragNode.key;
    const dropPos = info.node.pos.split('-');
    const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);

    const loop = (data, key, callback) => {
      for (let i = 0; i < data.length; i++) {
        if (data[i].key === key) {
          return callback(data[i], i, data);
        }
        if (data[i].children) {
          loop(data[i].children, key, callback);
        }
      }
    };

    // Fonction pour remonter aux parents avec `draw: true`
    const findAllBoxesInColumn = (columnId) => {

      return allBoxes.filter((box) => {
        if (box.columnId === columnId) {
          return box;
        }
      })
    }

    const data = [...gData];
    let dragObj;
    let boxesInColumn = [];

    // Remove the dragged node from its original position
    loop(data, dragKey, (item, index, arr) => {
      arr.splice(index, 1);
      const column = columns.find(column => column.id === item.columnId);
      if (column.drawMode) {

        boxesInColumn = findAllBoxesInColumn(item.columnId);
      }
      dragObj = item;
    });

    // Handle the drop based on whether it's dropped into a gap or as a child
    if (!info.dropToGap) {
      loop(data, dropKey, (item, index, arr) => {
        item.children = item.children || [];
        item.children.unshift(dragObj);

        // Update the indexTitle to reflect its new position as a child
        dragObj.indexTitle = [...item.indexTitle, item.children.length];

      });

    } else {
      let ar = [];
      let i;
      loop(data, dropKey, (_item, index, arr) => {
        ar = arr;
        i = index;
      });
      if (dropPosition === -1) {
        ar.splice(i, 0, dragObj);
      } else {
        ar.splice(i + 1, 0, dragObj);
      }

      dragObj.indexTitle = [i + 1];  // Update this logic depending on your tree structure
    }

    const typeTitle = dropPosition === 0 ? TITLE_TYPE_MAP[info.node.indexTitle.length] : info.node.type;

    const res = await moveBoxesIndex({ oldDragTitle: dragObj, aboveDropTitle: info, dropPosition, boxesInColumn });
    if (res !== undefined && res !== false) {
      openNotification(res);
    }

    const sameDrawModeColumn =
      dragObj?.columnId === info?.node?.columnId &&
      columns.find((c) => c.id === dragObj?.columnId)?.drawMode;

    // On autorise le changement de niveau dès lors que :
    // - le titre ne vient pas d'une section liée
    // - et qu'on ne reste pas dans la même colonne en draw-mode
    if (!dragObj?.from_linked_section && !sameDrawModeColumn) {
      updateBox(dragObj.id, (box) => {
        box.content.indexTitle = info.node.indexTitle;
        box.type = typeTitle;
      })
      selectLevelTitle(typeTitle, { boxId: dragObj.id, updateBox, batchUpdateBox, boxes, defaultConfiguration, updateIndexTitle, selectedBox: allBoxes.find(box => box.id === dragObj.id) });
    }

  };

  const handleOnDrop = async (info) => {
    await onDrop(info).then(() => {
      updateIndexTitle();
    });
  };

  const handleDeleteTitleAndThisContent = useCallback((node) => {
    const containerOfThisNode = containers.find(container => container.columnsIds.includes(node.columnId));

    if (containerOfThisNode.sectionId) {
      runSummaryDeleteConfirm(modalApi, {
        deleteSectionContainers: () => removeSectionContainers(node.fromBoxId, { sectionId: containerOfThisNode?.sectionId, dateTimestamp: containerOfThisNode?.dateTimestamp }),
        deleteContainer: () => removeContainer(containerOfThisNode.id),
        t,
        fromSection: true,
        fromLinkedSection: containerOfThisNode?.from_linked_section
      })
    }
    else if (containerOfThisNode?.KeyPdfToImage) {
      // Page d'un PDF importé : on propose aussi de retirer toutes ses pages.
      const { KeyPdfToImage } = containerOfThisNode;
      runSummaryDeleteConfirm(modalApi, {
        deleteContainer: () => {
          moveBoxesIndex({ oldDragTitle: node, aboveDropTitle: null, forDelete: true });
          updateIndexTitle();
        },
        KeyPdfToImage,
        deleteAllContainerFromPdfToImageWithSameKey: () => {
          removePdfImportContainers(KeyPdfToImage);
          updateIndexTitle();
        },
        t
      })
    }
    else {
      runSummaryDeleteConfirm(modalApi, {
        deleteContainer: () => {
          moveBoxesIndex({ oldDragTitle: node, aboveDropTitle: null, forDelete: true });
          updateIndexTitle();
        }, t
      })
    }
  }, [containers, removeSectionContainers, removeContainer, removePdfImportContainers, moveBoxesIndex, updateIndexTitle, t]);


  // Stable reference: only changes when drag state changes, preventing full tree re-renders
  const titleRender = useCallback((node) => {
    const isDragHighlighted = currentDragTitle !== null &&
      (
        (node.from_linked_section && node.fromSectionId === currentDragTitle?.fromSectionId) ||
        (currentDragTitle?.column.drawMode && node.column.id === currentDragTitle?.column.id)
      );
    const isSelectedHighlighted = isSummaryTitleHighlighted(node, { selectedSummaryTitle });
    const highlightStyle = (isDragHighlighted || isSelectedHighlighted)
      ? { color: getPrimaryDark(currentDocument?.type === 'template'), fontWeight: 'bold' }
      : {};

    if (isDragHighlighted) {
      return <div style={highlightStyle}>{node.title}</div>;
    }
    return (
      <div style={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }} className='container-title-summary'>
        <div style={{ maxWidth: 180, overflowWrap: "anywhere", fontStyle: node.isMissingParent ? "italic" : "normal", opacity: node.isMissingParent ? 0.7 : 1, ...highlightStyle }}>{node.title || "(vide)"}</div>
        <div style={{ width: 20 }}>
          {/* Fond rose porté par le span et croix en SVG de la taille du span :
              le viewBox centre le glyphe. Deux icônes empilées en fa-layers,
              de largeurs différentes et arrondies au pixel chacune de leur
              côté, laissaient la croix décentrée dans le carré. */}
          <span
            className='trash-icon'
            style={{ width: 16, height: 16, marginLeft: 4, borderRadius: 2, backgroundColor: "#F6A3AD" }}
            onClick={() => handleDeleteTitleAndThisContent(node)}
          >
            <FontAwesomeIcon
              icon={icon({ name: 'xmark', style: 'solid' })}
              style={{ display: 'block', width: '100%', height: '100%', color: "red" }}
              transform="shrink-2.5" />
          </span>
        </div>
      </div>
    );
  }, [currentDragTitle, selectedSummaryTitle, handleDeleteTitleAndThisContent, currentDocument]);

  // No dependencies: TITLE_4 check is pure
  const allowDrop = useCallback(({ dropNode }) => dropNode.type !== ItemTypes.TITLE_4, []);

  const onDragStart = useCallback((info) => setCurrentDragTitle(info.node), []);
  const onDragEnd = useCallback(() => setCurrentDragTitle(null), []);

  if (gData.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        style={{ marginTop: 12 }}
        description={`Aucun sommaire : ${documentKindLabel} ne comporte aucun titre.`}
      />
    );
  }

  return (
    <>
      {contextHolder}
      <Tree
        style={{ backgroundColor: PANEL_LIGHT_BG, color: PRIMARY_DARK, "--custom-indent-size": "40px" }}
        className="draggable-tree custom-tree"
        defaultExpandedKeys={expandedKeys}
        draggable={{ icon: false }}
        selectedKeys={selectedSummaryTitle?.key ? [selectedSummaryTitle.key] : []}
        onSelect={(_, { node }) => {
          onSummaryTitleSelect({
            key: node.key,
            id: node.id,
            fromSectionId: node.fromSectionId,
            from_linked_section: node.from_linked_section,
            linkedSectionId: node.from_linked_section ? node.fromSectionId : null,
          });
          unCollapseTitles({ index: node.indexTitle[0] });
          scrollToItem(node, containersToShow);
        }}
        onDragEnter={onDragEnter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        connecctDragPreview={false}
        allowDrop={allowDrop}
        titleRender={titleRender}
        onDrop={handleOnDrop}
        showLine
        switcherIcon={<DownOutlined style={{ color: "#aeaeae" }} />}
        treeData={gData}
      />
    </>
  );
};

export default React.memo(SummarySide);
